import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toastSuccess } from '../utils/toasts';
import Switch from './atoms/Switch';
import Input from './atoms/Input';
import Button from './atoms/Button';
import { CreateBankAccountDTO, BankAccountDTO } from '../models/bank-account';
import useGetParams from '../hooks/useGetParams';
import { useCreateBankAccountMutation, useUpdateBankAccountMutation } from '../store';

interface CreateEditBankAccountProps {
  onSuccess: () => void;
  bankAccount?: BankAccountDTO;
}

const validationSchema = yup.object().shape({
  status: yup.string().oneOf(['active', 'inactive']).required('El estado es requerido'),
  holder_name: yup.string().required('El nombre del titular es requerido'),
  transfer_code: yup.string().required('El CBU/CVU es requerido'),
  entity_name: yup.string().required('El banco es requerido'),
});

const CreateEditBankAccount = ({ onSuccess, bankAccount }: CreateEditBankAccountProps) => {
  const {
    register, handleSubmit, watch, setValue, formState: { errors },
  } = useForm<CreateBankAccountDTO>({
    defaultValues: {
      holder_name: bankAccount?.holder_name,
      transfer_code: bankAccount?.transfer_code,
      entity_name: bankAccount?.entity_name,
      status: bankAccount?.status || 'active',
    },
    resolver: yupResolver<CreateBankAccountDTO>(validationSchema),
  });
  const { businessId } = useGetParams();
  const [createBankAccount, resCreateBankAccount] = useCreateBankAccountMutation();
  const [updateBankAccount, resUpdateBankAccount] = useUpdateBankAccountMutation();

  const onSubmit = (formData: CreateBankAccountDTO) => {
    if (bankAccount) {
      updateBankAccount({
        businessId,
        bankAccountData: {
          id: bankAccount.id,
          holder_name: formData.holder_name,
          transfer_code: formData.transfer_code,
          entity_name: formData.entity_name,
          status: formData.status,
        },
      });
    } else {
      createBankAccount({
        businessId,
        bankAccountData: {
          holder_name: formData.holder_name,
          transfer_code: formData.transfer_code,
          entity_name: formData.entity_name,
          status: formData.status ? 'active' : 'inactive',
        },
      });
    }
  };

  useEffect(() => {
    if (resCreateBankAccount.isSuccess) {
      onSuccess();
      toastSuccess('Cuenta creada de banco');
    }
    if (resUpdateBankAccount.isSuccess) {
      onSuccess();
      toastSuccess('Cuenta actualizada');
    }
  }, [resCreateBankAccount.isSuccess, resUpdateBankAccount.isSuccess]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
      <Switch
        {...register('status')}
        id="status"
        label="Cuenta habilitada"
        checked={watch('status') === 'active'}
        onChange={(e) => setValue('status', e.target.checked ? 'active' : 'inactive')}
      />
      <Input
        {...register('holder_name')}
        id="holder_name"
        label="Nombre del titular *"
        labelAbove
        maxLength={50}
        errMsg={errors.holder_name?.message}
      />
      <Input
        {...register('transfer_code')}
        id="transfer_code"
        label="CBU/CVU o alias *"
        labelAbove
        maxLength={30}
        errMsg={errors.transfer_code?.message}
      />
      <Input
        {...register('entity_name')}
        id="entity_name"
        label="Banco *"
        labelAbove
        maxLength={50}
        errMsg={errors.entity_name?.message}
      />
      <div className='flex flex-row gap-2'>
        <Button variant='secondary' onClick={onSuccess}>Cancelar</Button>
        <Button variant='primary' type='submit' loading={resCreateBankAccount.isLoading || resUpdateBankAccount.isLoading}>Guardar</Button>
      </div>
    </form>
  );
};

export default CreateEditBankAccount;
